import styles from "./ParameterApplyModalContent.module.css";
import modal from "components/Modal/ModalContent.module.css";
import cn from "classnames";
import { Button } from "components/Button/Button";
import { IconButton } from "components/Button/IconButton";
import { IconCross } from "components/Icons/IconCross";
import { Toggle } from "gui/Toggle/Toggle";
import { Notification } from "components/Notification";

import { useState } from "react";

import type { ActiveContext } from "shared/ContextSwitcher/store";

export function ParameterApplyModalContent(props: {
  activeContext: ActiveContext;

  getCost: (queryCount: number) => number;
  queryIds: number[];
  queryIdsWithNonDefaultParamsThatDidNotJustRun: number[];
  unexecutableQueryCount: number;

  runAllQueries: () => void;
  runQueriesWithNonDefaultParams: () => void;

  close: () => void;
}) {
  const {
    activeContext,
    getCost,
    queryIdsWithNonDefaultParamsThatDidNotJustRun,
    queryIds,
    unexecutableQueryCount,
    runAllQueries,
    runQueriesWithNonDefaultParams,
  } = props;

  const [runAll, setRunAll] = useState(false);

  const count = queryIdsWithNonDefaultParamsThatDidNotJustRun.length;

  return (
    <div className={cn(modal.body)}>
      <div className={modal.header}>
        <div className={modal.titleRow}>
          <h2>Run with selected parameters</h2>

          <IconButton size="XS" theme="ghost" onClick={props.close}>
            <IconCross />
          </IconButton>
        </div>

        <p>
          This will {count < queryIds.length && <strong>only</strong>} execute
          the {count === 1 ? "query which is" : `${count} queries which are`}{" "}
          affected by the parameter selection.{" "}
          {queryIdsWithNonDefaultParamsThatDidNotJustRun.join(", ")}
        </p>
      </div>

      <div className={cn(modal.content, styles.content)}>
        {queryIds.length >
          queryIdsWithNonDefaultParamsThatDidNotJustRun.length && (
          <Toggle
            label={
              <>
                Execute all {queryIds.length}
                {unexecutableQueryCount > 0 ? " executable* " : " "}queries and
                apply new parameters
              </>
            }
            description={
              activeContext.serviceTier.version === "v2" && (
                <>
                  Running all queries will use {getCost(queryIds.length)}{" "}
                  credits from @{activeContext.name} plan quota.
                </>
              )
            }
            enabled={runAll}
            setEnabled={setRunAll}
          />
        )}

        {unexecutableQueryCount > 0 && (
          <Notification
            level="warning"
            dismissable={false}
            title="*Deprecated datasets"
            description={
              <>
                {unexecutableQueryCount} queries on this dashboard can no longer
                be executed because they run against deprecated datasets.
              </>
            }
          />
        )}

        {activeContext.serviceTier.version === "v2" ? (
          <Notification
            dismissable={false}
            level="info"
            title={
              <>
                These executions will use{" "}
                {runAll ? getCost(queryIds.length) : getCost(count)} credits
                from @{activeContext.name} plan quota.
              </>
            }
          />
        ) : (
          <Notification
            dismissable={false}
            level="info"
            title={
              <>
                These queries will be executed on your @{activeContext.name}{" "}
                plan quota.
              </>
            }
          />
        )}
      </div>

      <div className={modal.buttons}>
        <Button size="M" theme="tertiary" onClick={props.close}>
          Close
        </Button>

        {runAll ? (
          <Button
            size="M"
            theme="primary"
            onClick={() => {
              runAllQueries();
              props.close();
            }}
          >
            Run all queries
          </Button>
        ) : (
          <Button
            size="M"
            theme="primary"
            autoFocus
            onClick={() => {
              runQueriesWithNonDefaultParams();
              props.close();
            }}
          >
            Run {count} {count === 1 ? "query" : "queries"}
          </Button>
        )}
      </div>
    </div>
  );
}
